import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import useApi from "../../../hooks/useApi";
import useData from "../../../hooks/useData";
import usePage from "../../../hooks/usePage";

const PAGE_SIZE = 24;
const DEFAULT_IMAGE_FOLDER = "survey-images";
const FOOTER_METADATA_FIELD_OPTIONS = [
  { id: "projectName", label: "Project" },
  { id: "surveyName", label: "Survey" },
  { id: "sectionName", label: "Section" },
  { id: "questionName", label: "Question" },
  { id: "skuName", label: "SKU" },
  { id: "imageType", label: "Image Type" },
  { id: "retailerName", label: "Retailer" },
  { id: "locationName", label: "Location Name" },
  { id: "fullAddress", label: "Full Address" },
  { id: "date", label: "Date" },
];

const normalizeImageKey = (imageKey) => {
  if (!imageKey) return null;
  if (typeof imageKey === "string") return imageKey;
  if (typeof imageKey === "object") {
    return (
      imageKey.imageKey ||
      imageKey.fileName ||
      imageKey.data ||
      imageKey.url ||
      imageKey.key ||
      imageKey.name ||
      null
    );
  }
  return String(imageKey);
};

const toStorageRef = (row) => {
  const rawKey = normalizeImageKey(row?.imageKey);
  if (!rawKey)
    return { storageKey: null, folder: null, key: null, rawKey: null };

  if (/^(https?:|data:|blob:)/i.test(rawKey)) {
    try {
      const parsed = new URL(rawKey);
      const pathKey = parsed.pathname.replace(/^\/+/, "");
      if (!pathKey) {
        return { storageKey: rawKey, folder: null, key: null, rawKey };
      }
      const [folder, ...rest] = pathKey.split("/");
      return {
        storageKey: pathKey,
        folder: folder || null,
        key: rest.join("/") || null,
        rawKey,
      };
    } catch {
      return { storageKey: rawKey, folder: null, key: null, rawKey };
    }
  }

  if (rawKey.includes("/")) {
    const [folder, ...rest] = rawKey.split("/");
    return { storageKey: rawKey, folder, key: rest.join("/"), rawKey };
  }

  const folder = row?.imageFolder || row?.folder || DEFAULT_IMAGE_FOLDER;
  return { storageKey: `${folder}/${rawKey}`, folder, key: rawKey, rawKey };
};

const ClientPortalImageGalleryModule = () => {
  const { executeAction } = useData();
  const { retrieveFile } = useApi();
  const { showMessage } = usePage();

  const [type, setType] = useState("");
  const [projectId, setProjectId] = useState("");
  const [surveyId, setSurveyId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [bannerId, setBannerId] = useState("");
  const [storeNumber, setStoreNumber] = useState("");
  const [projects, setProjects] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [sections, setSections] = useState([]);
  const [banners, setBanners] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [hiddenStorageKeys, setHiddenStorageKeys] = useState({});
  const [footerMetadataFields, setFooterMetadataFields] = useState(
    FOOTER_METADATA_FIELD_OPTIONS.map((item) => item.id),
  );

  const buildFilterPayload = () => {
    const payload = {};
    if (type) payload.type = type;
    if (projectId) payload.projectId = projectId;
    if (surveyId) payload.surveyId = surveyId;
    if (sectionId) payload.sectionId = sectionId;
    if (bannerId) payload.bannerId = bannerId;
    if (storeNumber && String(storeNumber).trim()) {
      payload.storeNumber = String(storeNumber).trim();
    }
    if (dateRange.startDate && dateRange.endDate) {
      payload.dateRange = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
    }
    return payload;
  };

  const hasActiveFilters =
    !!type ||
    !!projectId ||
    !!surveyId ||
    !!sectionId ||
    !!bannerId ||
    !!(storeNumber && String(storeNumber).trim()) ||
    !!(dateRange.startDate && dateRange.endDate);

  const hydrateUrls = async (rows) => {
    const pending = rows
      .map((row) => ({ row, ref: toStorageRef(row) }))
      .filter(({ ref }) => ref.storageKey && !(ref.storageKey in imageUrls));

    if (pending.length === 0) return;

    const fetches = pending.map(async ({ ref }) => {
      try {
        const url =
          ref.folder && ref.key
            ? await retrieveFile(ref.folder, ref.key)
            : await retrieveFile(ref.storageKey);
        return { storageKey: ref.storageKey, url };
      } catch {
        return { storageKey: ref.storageKey, url: null };
      }
    });

    const results = await Promise.all(fetches);
    setImageUrls((prev) => {
      const next = { ...prev };
      for (const entry of results) {
        next[entry.storageKey] = entry.url || null;
      }
      return next;
    });
  };

  const loadImages = async (page = pageNumber) => {
    setLoading(true);
    const result = await executeAction({
      entityName: "Client",
      action: "getMyPortalImageGallery",
      data: {
        filter: buildFilterPayload(),
        pageNumber: page,
        recordsPerPage: PAGE_SIZE,
      },
    });

    if (result?.isSuccessful) {
      const rows = Array.isArray(result?.data?.rows) ? result.data.rows : [];
      setImages(rows);
      setTotal(Number(result?.data?.total || 0));
      setHasMore(result?.data?.hasMore === true);
      setHiddenStorageKeys({});
      await hydrateUrls(rows);
    } else {
      setImages([]);
      setTotal(0);
      setHasMore(false);
      showMessage(result?.message || "Unable to load images");
    }

    setLoading(false);
  };

  const loadFilterOptions = async () => {
    const result = await executeAction({
      entityName: "Client",
      action: "getMyPortalImageGalleryFilters",
    });

    if (!result?.isSuccessful) {
      setProjects([]);
      setSurveys([]);
      setSections([]);
      setBanners([]);
      return;
    }

    setProjects(
      Array.isArray(result?.data?.projects) ? result.data.projects : [],
    );
    setSurveys(Array.isArray(result?.data?.surveys) ? result.data.surveys : []);
    setSections(
      Array.isArray(result?.data?.sections) ? result.data.sections : [],
    );
    setBanners(Array.isArray(result?.data?.banners) ? result.data.banners : []);
  };

  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) {
      if (surveyId) setSurveyId("");
      if (sectionId) setSectionId("");
      return;
    }
    if (!surveyId) {
      if (sectionId) setSectionId("");
      return;
    }
    const surveyExistsForProject = surveys.some(
      (survey) => survey.id === surveyId && survey.projectId === projectId,
    );
    if (!surveyExistsForProject) {
      setSurveyId("");
      if (sectionId) setSectionId("");
    }
  }, [projectId, surveyId, sectionId, surveys]);

  useEffect(() => {
    if (!sectionId) return;
    if (!projectId || !surveyId) {
      setSectionId("");
      return;
    }
    const sectionExists = sections.some(
      (section) =>
        section.id === sectionId &&
        section.surveyId === surveyId &&
        section.projectId === projectId,
    );
    if (!sectionExists) {
      setSectionId("");
    }
  }, [projectId, surveyId, sectionId, sections]);

  const filteredSurveys = projectId
    ? surveys.filter((survey) => survey.projectId === projectId)
    : [];

  const filteredSections =
    projectId && surveyId
      ? sections.filter(
          (section) =>
            section.projectId === projectId && section.surveyId === surveyId,
        )
      : [];

  const filteredBanners = projectId
    ? banners.filter(
        (banner) =>
          Array.isArray(banner.projectIds) &&
          banner.projectIds.includes(projectId),
      )
    : banners;

  useEffect(() => {
    if (!bannerId) return;
    const exists = filteredBanners.some((banner) => banner.id === bannerId);
    if (!exists) {
      setBannerId("");
    }
  }, [bannerId, filteredBanners]);

  const handleExport = async () => {
    setExporting(true);
    const result = await executeAction({
      entityName: "Client",
      action: "generateMyPortalImageGallery",
      data: {
        filter: buildFilterPayload(),
        metadataFields: footerMetadataFields,
      },
      displayMessage: true,
    });
    setExporting(false);

    if (!result?.isSuccessful) {
      showMessage(result?.message || "Failed to queue gallery export");
    }
  };

  const renderImageCard = (row, index) => {
    const { storageKey } = toStorageRef(row);
    if (!storageKey || hiddenStorageKeys[storageKey]) return null;

    const url = imageUrls[storageKey];
    if (url === null) return null;

    const metadata = row.metadata || {};
    const hasMeta = (field) => footerMetadataFields.includes(field);
    const cityProv = [metadata.locationCity, metadata.locationProvince]
      .filter(Boolean)
      .join(", ");
    const locationNameDisplay = String(metadata.locationName || "").trim();
    const locationNumberDisplay = String(metadata.locationNumber || "").trim();
    const streetAddressLine = [
      metadata.address?.streetAddress,
      metadata.address?.otherAddress,
      metadata.address?.line1,
      metadata.address?.line2,
    ]
      .filter(Boolean)
      .join(", ");
    const cityProvincePostalLine = [
      metadata.locationCity || metadata.address?.city,
      metadata.locationProvince || metadata.address?.province,
      metadata.address?.postal || metadata.address?.postalCode,
    ]
      .filter(Boolean)
      .join(", ");
    const scheduleDate = metadata.scheduleDate
      ? new Date(metadata.scheduleDate).toLocaleDateString()
      : "";

    return (
      <Card
        key={`${storageKey || "image"}-${index}`}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            backgroundColor: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 220,
          }}
        >
          {url ? (
            <img
              src={url}
              alt={metadata.questionName || "image"}
              loading="lazy"
              onError={() => {
                setHiddenStorageKeys((prev) => ({
                  ...prev,
                  [storageKey]: true,
                }));
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "220px",
                objectFit: "contain",
              }}
            />
          ) : (
            <CircularProgress size={24} />
          )}
        </Box>
        <CardContent
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            {hasMeta("imageType") && (
              <Chip
                label={row.imageType}
                size="small"
              />
            )}
          </Stack>

          {hasMeta("projectName") && (
            <Typography variant="subtitle2">{metadata.projectName}</Typography>
          )}
          {hasMeta("surveyName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.surveyName}
            </Typography>
          )}
          {hasMeta("sectionName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.sectionName}
            </Typography>
          )}
          {hasMeta("questionName") && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.questionName}
            </Typography>
          )}
          {hasMeta("skuName") && metadata.skuName && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {metadata.skuName}
            </Typography>
          )}
          {hasMeta("locationName") &&
            (locationNameDisplay || locationNumberDisplay) && (
              <>
                {locationNameDisplay && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {locationNameDisplay}
                  </Typography>
                )}
                {locationNumberDisplay && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    #{locationNumberDisplay}
                  </Typography>
                )}
              </>
            )}
          {hasMeta("fullAddress") &&
            (streetAddressLine || cityProvincePostalLine) && (
              <>
                {streetAddressLine && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {streetAddressLine}
                  </Typography>
                )}
                {cityProvincePostalLine && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {cityProvincePostalLine}
                  </Typography>
                )}
              </>
            )}
          {!hasMeta("fullAddress") && cityProv && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {cityProv}
            </Typography>
          )}
          {hasMeta("date") && scheduleDate && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {scheduleDate}
            </Typography>
          )}

          <Box sx={{ mt: "auto", display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              href={url || "#"}
              target="_blank"
              rel="noreferrer"
              disabled={!url}
            >
              View
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const canGoNext = hasMore;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2 }}
        >
          Image Gallery
        </Typography>

        <Grid
          container
          spacing={2}
          alignItems="center"
        >
          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 150 }}
            >
              <InputLabel id="client-gallery-banner-label">Banner</InputLabel>
              <Select
                labelId="client-gallery-banner-label"
                label="Banner"
                value={bannerId}
                onChange={(e) => setBannerId(e.target.value)}
              >
                <MenuItem value="">All Banners</MenuItem>
                {filteredBanners.map((banner) => (
                  <MenuItem
                    key={banner.id}
                    value={banner.id}
                  >
                    {banner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <TextField
              label="Store Number"
              size="small"
              fullWidth
              value={storeNumber}
              onChange={(e) => setStoreNumber(e.target.value)}
            />
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 150 }}
            >
              <InputLabel id="client-gallery-project-label">Project</InputLabel>
              <Select
                labelId="client-gallery-project-label"
                label="Project"
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                }}
              >
                <MenuItem value="">All Projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem
                    key={project.id}
                    value={project.id}
                  >
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 150 }}
            >
              <InputLabel id="client-gallery-survey-label">Survey</InputLabel>
              <Select
                labelId="client-gallery-survey-label"
                label="Survey"
                value={surveyId}
                disabled={!projectId}
                onChange={(e) => setSurveyId(e.target.value)}
              >
                <MenuItem value="">All Surveys</MenuItem>
                {filteredSurveys.map((survey) => (
                  <MenuItem
                    key={survey.id}
                    value={survey.id}
                  >
                    {survey.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 150 }}
            >
              <InputLabel id="client-gallery-section-label">Section</InputLabel>
              <Select
                labelId="client-gallery-section-label"
                label="Section"
                value={sectionId}
                disabled={!projectId || !surveyId}
                onChange={(e) => setSectionId(e.target.value)}
              >
                <MenuItem value="">All Sections</MenuItem>
                {filteredSections.map((section) => (
                  <MenuItem
                    key={section.id}
                    value={section.id}
                  >
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <TextField
              label="Start date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <TextField
              label="End date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 150 }}
            >
              <InputLabel id="client-gallery-type-label">Image Type</InputLabel>
              <Select
                labelId="client-gallery-type-label"
                label="Image Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="image">Standard</MenuItem>
                <MenuItem value="beforeAfter">Before/After</MenuItem>
                <MenuItem value="distribution">Distribution</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              variant="contained"
              fullWidth
              disabled={loading || !hasActiveFilters}
              onClick={() => {
                setPageNumber(1);
                loadImages(1);
              }}
            >
              {loading ? "Loading" : "Apply"}
            </Button>
          </Grid>

          <Grid
            xs={12}
            sm={6}
            md={2}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setType("");
                setProjectId("");
                setSurveyId("");
                setSectionId("");
                setBannerId("");
                setStoreNumber("");
                setDateRange({ startDate: "", endDate: "" });
                setPageNumber(1);
                setImages([]);
                setTotal(0);
                setHasMore(false);
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ width: "100%" }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1 }}
          >
            Footer Metadata
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Default is all metadata. If none is selected, no footer will be
            added to exported images.
          </Typography>
          <FormGroup row>
            {FOOTER_METADATA_FIELD_OPTIONS.map((item) => {
              const checked = footerMetadataFields.includes(item.id);
              return (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFooterMetadataFields((prev) => {
                          if (isChecked) {
                            return Array.from(new Set([...prev, item.id]));
                          }
                          return prev.filter((field) => field !== item.id);
                        });
                      }}
                    />
                  }
                  label={item.label}
                />
              );
            })}
          </FormGroup>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Queuing..." : "Export Gallery"}
          </Button>
        </Box>
      </Paper>

      <Box>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">Images</Typography>
          <Chip
            label={`${images.length} items`}
            size="small"
          />
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading images...</Typography>
          </Box>
        )}

        {!loading && !hasActiveFilters && (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Select at least one filter, then click Apply.
          </Typography>
        )}

        {!loading && hasActiveFilters && images.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            No images found for the current filters.
          </Typography>
        )}

        <Grid
          container
          spacing={2}
        >
          {images.map((row, idx) => {
            const { storageKey } = toStorageRef(row);
            return (
              <Grid
                key={`${storageKey || "image"}-${idx}`}
                xs={12}
                sm={6}
                md={4}
              >
                {renderImageCard(row, idx)}
              </Grid>
            );
          })}
        </Grid>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
        >
          <Button
            variant="outlined"
            disabled={pageNumber === 1 || loading || !hasActiveFilters}
            onClick={() => {
              const next = Math.max(1, pageNumber - 1);
              setPageNumber(next);
              loadImages(next);
            }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            disabled={!canGoNext || loading || !hasActiveFilters}
            onClick={() => {
              const next = pageNumber + 1;
              setPageNumber(next);
              loadImages(next);
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default ClientPortalImageGalleryModule;
